import * as React from "react";
import { useParams } from "react-router";
import {
  ContentResultTable,
  ContentResultTableRow,
} from "../../../components/features/ContentResultTable";
import { ContentResultTemplate } from "../../../components/features/ContentResultTemplate";
import { OutletContextProps } from "../../../helpers/RouterHelper";
import enhanceSectionsSettingsPage from "../../../hocs/enhanceSectionsSettingsPage";
import { useFetchContentCourseUnitResults } from "../../../hooks/http/useFetchContentCourseUnitResults";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { useOnScrollContentBottom } from "../../../hooks/useOnScrollContentBottom";
import SectionInterface from "../../../interfaces/SectionInterface";

const useContentCourseUnitResult = ({ sectionId }: { sectionId: string }) => {
  const { contentCourseId: courseId = "" } = useParams<{
    contentCourseId: string;
  }>();
  const fetchResult = useFetchContentCourseUnitResults({
    sectionId: sectionId,
    courseId: courseId,
  });
  useQueryError(fetchResult.error);
  const breadcrumbsProps = fetchResult.data && {
    items: [
      {
        label: "学習結果",
        url: `/sections/${sectionId}/settings/content_courses/results`,
      },
      {
        label: fetchResult.data.name,
      },
    ],
  };

  return {
    breadcrumbsProps,
    ...fetchResult,
  };
};

type Props = OutletContextProps & {
  section: SectionInterface;
};
const ContentCourseUnitResultPage = (props: Props) => {
  React.useEffect(() => {
    props.setActiveMenu("courses");
  }, []);
  const {
    data,
    breadcrumbsProps,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useContentCourseUnitResult({
    sectionId: props.section.id,
  });
  const rootRef = React.useRef(null);
  const { refLastElement } = useOnScrollContentBottom({
    onScrollContentBottom() {
      !isLoading && hasNextPage && fetchNextPage();
    },
    rootContentWithScroll: rootRef.current,
  });

  if (!data || !breadcrumbsProps) {
    return null;
  }

  const columns = data.units.map((unit) => ({
    ...unit,
    url: `/sections/${props.section.id}/settings/content_courses/${data.id}/units/${unit.id}/results`,
  }));
  return (
    <ContentResultTemplate
      sectionId={props.section.id}
      courseId={data.id}
      title={data.name}
      subText={`ユニット数：${data.units.length}`}
      breadcrumbsProps={breadcrumbsProps}
      isLoading={isLoading || isFetchingNextPage}
    >
      <ContentResultTable columns={columns} ref={rootRef}>
        {data.data.map((e, i) => (
          <ContentResultTableRow
            key={`ContentResultTableRow-${e.student.id}`}
            student={e.student}
            results={e.courseResult}
            scores={e.courseResult.unitResults}
            rank={i + 1}
            totalCount={data.meta.totalCount}
            courseId={data.id}
            columns={columns}
            ref={refLastElement}
          />
        ))}
      </ContentResultTable>
    </ContentResultTemplate>
  );
};

const pageInfo = {
  title: "学習結果一覧",
};
export default enhanceSectionsSettingsPage(
  ContentCourseUnitResultPage,
  pageInfo,
);
